import { useEffect, useState, useCallback, useRef } from "react";
import API from "../../services/api";
import {
  Row,
  Col,
  Spin,
  Select,
  Button,
  Empty,
  Space,
  Pagination,
  Modal,
  AutoComplete,
} from "antd";
import {
  FilterOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import "./Stalls.css";
import { filterCities, getLatLngForFilter } from "../locationService";
import {
  validateLocality,
  validateNumericRange,
  validateCustomText,
  isEmpty,
} from "../../utils/Filtervalidation.js";
import { useFilterValidation } from "../../hooks/Usefiltervalidation.js";
// ─────────────────────────────────────────────────────────────────────────────
// NEW: reusable location hook
// ─────────────────────────────────────────────────────────────────────────────
import { useLocationSearch } from "../../hooks/Uselocationsearch.js";

// ─────────────────────────────────────────────────────────────────────────────
// ALLOWED VALUES — prevents injection of arbitrary strings into API params
// ─────────────────────────────────────────────────────────────────────────────
const ALLOWED_STALL_TYPES = ["food", "retail", "exhibition", "service", "other"];
const ALLOWED_PRICING_TYPES = ["rent", "lease", "sale"];
const ALLOWED_SORT_BY = ["price_low", "price_high", "area_high"];
const ALLOWED_FACILITIES = ["Power Supply", "Water Supply", "Parking"];

const DURATION_TYPES_BY_PRICING = {
  rent: [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ],
  lease: [
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ],
  sale: [],
};

const ALL_DURATION_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const SORT_OPTIONS = [
  {
    value: "price_low",
    label: "Price Low to High",
  },
  {
    value: "price_high",
    label: "Price High to Low",
  },
  {
    value: "area_high",
    label: "Largest Area",
  },
];

const MAX_PRICE = 1_000_000_000;
const MAX_AREA = 10_000_000;

// ─────────────────────────────────────────────────────────────────────────────
// ValidationTooltip — native-browser-style tooltip shown below each field
// ─────────────────────────────────────────────────────────────────────────────
function ValidationTooltip({ message }) {
  if (!message) return null;
  return (
    <div className="vt-tooltip" role="alert" aria-live="polite">
      <span className="vt-tooltip-icon" aria-hidden="true" />
      <span className="vt-tooltip-text">{message}</span>
    </div>
  );
}

function Stalls() {
  const [stalls, setStalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);

  // ── Search-bar suggestion state (driven by useLocationSearch hook)
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  // ── Filter-modal locality suggestion state (separate, unchanged)
  const [filterSuggestions, setFilterSuggestions] = useState([]);

  const [customStallTypeFilter, setCustomStallTypeFilter] = useState("");

  const [pricingDropdownOpen, setPricingDropdownOpen] = useState(false);

  const [localityDropdownOpen, setLocalityDropdownOpen] = useState(false);

  const [stallTypeDropdownOpen, setStallTypeDropdownOpen] = useState(false);

  const [durationDropdownOpen, setDurationDropdownOpen] = useState(false);

  const [facilitiesDropdownOpen, setFacilitiesDropdownOpen] = useState(false);

  const [negotiableDropdownOpen, setNegotiableDropdownOpen] = useState(false);

  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  // ── Validation hook ────────────────────────────────────────────────────────
  const { fieldErrors, setFieldErrors, clearAllFieldErrors, scrollToFirstError } =
    useFilterValidation();

  // ── Refs for scroll-to-error ───────────────────────────────────────────────
  const localityRef = useRef(null);
  const priceRef = useRef(null);
  const areaRef = useRef(null);
  const customStallTypeRef = useRef(null);

  // Responsive modal width
  const [modalWidth, setModalWidth] = useState(600);

  // filterDebounceRef is still used by the filter-modal locality field
  const filterDebounceRef = useRef(null);

  const filterContentRef = useRef(null);

  const searchDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 576) {
        setModalWidth("96vw");
      } else if (window.innerWidth <= 768) {
        setModalWidth("95vw");
      } else {
        setModalWidth(600);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setIsAdmin(user?.role === "admin");
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    locality: "",
    latitude: null,
    longitude: null,
    stallType: "",
    pricingType: "",
    durationType: "",
    minPrice: "",
    maxPrice: "",
    minArea: "",
    maxArea: "",
    facilities: [],
    negotiable: null,
    sortBy: "",
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  // ── NEW: reusable location-search hook for the main search bar ─────────────
  const {
    inputValue: searchInputValue,
    isLocationValid: isSearchLocationValid,
    handleChange: handleSearchChange,
    handleSelect: handleSearchSelect,
    handleKeyDown: makeSearchKeyDown,
    handleFocus: handleSearchFocus,
    resetLocation: resetSearchLocation,
  } = useLocationSearch({
    onSuggestionsChange: setSearchSuggestions,
    debounceMs: 300,
    minChars: 2,

    // SYNC FILTER → SEARCH
    syncedLocality: filters.locality,

    // SYNC SEARCH → FILTER
    onSyncLocality: (value) => {

      setFilters((prev) => {

        if (prev.locality === value) {
          return prev;
        }

        return {
          ...prev,
          locality: value,
        };
      });
    },
  });

  // ── Sanitize a numeric filter value before storing in state ──────────────
  const sanitizeNumericInput = (value, max) => {
    if (value === "" || value === null || value === undefined) {
      return "";
    }

    // ONLY positive integers allowed
    const stripped = String(value).replace(/[^0-9]/g, "");

    if (stripped === "") {
      return "";
    }

    const n = Number(stripped);

    if (!Number.isInteger(n) || n < 0) {
      return "";
    }

    if (max !== undefined && n > max) {
      return String(max);
    }

    return stripped;
  };

  // ── Build clean params object from current filters ───────────────────────
  const buildParams = useCallback((customFilters, page, pageSize) => {
    const params = { page, limit: pageSize };

    if (customFilters.search?.trim()) {
      params.search = customFilters.search.trim().slice(0, 200);
    }
    if (customFilters.locality?.trim()) {
      params.locality = customFilters.locality.trim().slice(0, 100);
    }

    if (customFilters.stallType) {
      params.stallType = String(customFilters.stallType).slice(0, 100);
    }

    if (
      customFilters.pricingType &&
      ALLOWED_PRICING_TYPES.includes(customFilters.pricingType)
    ) {
      params.pricingType = customFilters.pricingType;
    }

    if (customFilters.durationType && customFilters.pricingType !== "sale") {
      const allowed = customFilters.pricingType
        ? (DURATION_TYPES_BY_PRICING[customFilters.pricingType] || []).map(
          (d) => d.value
        )
        : ALL_DURATION_OPTIONS.map((d) => d.value);

      if (allowed.includes(customFilters.durationType)) {
        params.durationType = customFilters.durationType;
      }
    }

    const minPrice = Number(customFilters.minPrice);
    const maxPrice = Number(customFilters.maxPrice);
    if (customFilters.minPrice !== "" && isFinite(minPrice) && minPrice >= 0) {
      params.minPrice = minPrice;
    }
    if (customFilters.maxPrice !== "" && isFinite(maxPrice) && maxPrice >= 0) {
      params.maxPrice = maxPrice;
    }

    const minArea = Number(customFilters.minArea);
    const maxArea = Number(customFilters.maxArea);
    if (customFilters.minArea !== "" && isFinite(minArea) && minArea >= 0) {
      params.minArea = minArea;
    }
    if (customFilters.maxArea !== "" && isFinite(maxArea) && maxArea >= 0) {
      params.maxArea = maxArea;
    }

    if (Array.isArray(customFilters.facilities) && customFilters.facilities.length) {
      const safeFacilities = customFilters.facilities.filter((f) =>
        ALLOWED_FACILITIES.includes(f)
      );
      if (safeFacilities.length) {
        params.facilities = safeFacilities.join(",");
      }
    }

    if (
      customFilters.negotiable !== null &&
      customFilters.negotiable !== undefined
    ) {
      params.negotiable = customFilters.negotiable;
    }

    if (customFilters.sortBy && ALLOWED_SORT_BY.includes(customFilters.sortBy)) {
      params.sortBy = customFilters.sortBy;
    }

    return params;
  }, []);

  const fetchStalls = useCallback(
    async (customFilters = filters, page = 1, pageSize = 15) => {
      try {
        setLoading(true);
        const params = buildParams(customFilters, page, pageSize);
        const res = await API.get("/stalls", { params });
        setStalls(res.data.stalls || []);
        setPagination({
          current: res.data.currentPage || 1,
          pageSize,
          total: res.data.total || 0,
        });
      } catch (err) {
        console.log("Fetch error:", err);
        setStalls([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchStalls(filters, 1, 15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Run per-field validation, collect errors, scroll to first ────────────
  const runFieldValidations = (filtersToValidate, customStallType) => {
    const errors = {};

    const localityResult = validateLocality(filtersToValidate.locality);
    if (!localityResult.valid) errors.locality = localityResult.message;

    const priceResult = validateNumericRange(
      filtersToValidate.minPrice,
      filtersToValidate.maxPrice,
      "Price"
    );
    if (!priceResult.valid) errors.price = priceResult.message;

    const areaResult = validateNumericRange(
      filtersToValidate.minArea,
      filtersToValidate.maxArea,
      "Area"
    );
    if (!areaResult.valid) errors.area = areaResult.message;

    if (filtersToValidate.stallType === "other") {
      const customResult = validateCustomText(customStallType, "Custom Stall Type");
      if (!customResult.valid) errors.customStallType = customResult.message;
    }

    setFieldErrors(errors);

    const refMap = {
      locality: localityRef,
      price: priceRef,
      area: areaRef,
      customStallType: customStallTypeRef,
    };
    scrollToFirstError(refMap, errors);

    return Object.keys(errors).length === 0;
  };

  // ── REAL-TIME ERROR CLEARING ─────────────────────────────────────────────
  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) return;

    const newErrors = { ...fieldErrors };

    // Locality
    if (newErrors.locality) {
      const localityResult = validateLocality(filters.locality);
      if (localityResult.valid) delete newErrors.locality;
    }

    // Price
    if (newErrors.price) {
      const priceResult = validateNumericRange(
        filters.minPrice,
        filters.maxPrice,
        "Price"
      );
      if (priceResult.valid) delete newErrors.price;
    }

    // Area
    if (newErrors.area) {
      const areaResult = validateNumericRange(
        filters.minArea,
        filters.maxArea,
        "Area"
      );
      if (areaResult.valid) delete newErrors.area;
    }

    // Custom Stall Type
    if (newErrors.customStallType) {
      const customResult = validateCustomText(
        customStallTypeFilter,
        "Custom Stall Type"
      );
      if (customResult.valid) delete newErrors.customStallType;
    }

    if (Object.keys(newErrors).length !== Object.keys(fieldErrors).length) {
      setFieldErrors(newErrors);
    }
  }, [
    filters.locality,
    filters.minPrice,
    filters.maxPrice,
    filters.minArea,
    filters.maxArea,
    customStallTypeFilter,
    fieldErrors,
    setFieldErrors,
  ]);

  useEffect(() => {
    const handleOutsideClick = (e) => {

      // SEARCH BAR DROPDOWN
      const clickedSearchArea = e.target.closest(
        ".st2-search-field"
      );

      if (!clickedSearchArea) {
        setSearchSuggestions([]);
      }

      // FILTER LOCALITY DROPDOWN
      const clickedFilterArea = e.target.closest(
        ".st2-filter-group"
      );

      if (!clickedFilterArea) {
        setFilterSuggestions([]);
      }

      // CUSTOM DROPDOWNS
      if (!e.target.closest(".st2-custom-dropdown")) {

        setPricingDropdownOpen(false);

        setLocalityDropdownOpen(false);

        setStallTypeDropdownOpen(false);

        setDurationDropdownOpen(false);

        setFacilitiesDropdownOpen(false);

        setNegotiableDropdownOpen(false);

        setSortDropdownOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  useEffect(() => {
    if (filterModalVisible) {
      setTimeout(() => {
        const modalBody = document.querySelector(
          ".st2-filter-modal .ant-modal-body"
        );

        modalBody?.scrollTo({
          top: 0,
          behavior: "auto",
        });
      }, 50);
    }
  }, [filterModalVisible]);

  // ── handleApplyFilters (filter modal) ────────────────────────────────────
  const handleApplyFilters = async () => {
    const isValid = runFieldValidations(filters, customStallTypeFilter);
    if (!isValid) return;

    let updatedFilters = {
      ...filters,
      search: "",
      locality: filters.locality?.trim() || "",
    };

    if (updatedFilters.stallType === "other") {
      const trimmedCustom = customStallTypeFilter.trim();
      updatedFilters.stallType = trimmedCustom ? trimmedCustom.slice(0, 100) : "";
    }

    if (!updatedFilters.locality) {
      updatedFilters.latitude = null;
      updatedFilters.longitude = null;
    }

    setFilters(updatedFilters);
    setSearchSuggestions([]);
    clearAllFieldErrors();
    await fetchStalls(updatedFilters, 1, pagination.pageSize);
    setFilterModalVisible(false);
  };

  // ── handleSearchBarApply (main search bar) ────────────────────────────────
  // Called ONLY when the Search button is clicked or Enter is pressed (valid).
  const handleSearchBarApply = async () => {

    const selectedValue = searchInputValue.trim();

    if (!selectedValue) {
      handleReset();
      return;
    }

    const updatedFilters = {
      search: isSearchLocationValid ? "" : selectedValue,
      locality: isSearchLocationValid ? selectedValue : "",
      latitude: null,
      longitude: null,
      stallType: "",
      pricingType: "",
      durationType: "",
      minPrice: "",
      maxPrice: "",
      minArea: "",
      maxArea: "",
      facilities: [],
      negotiable: null,
      sortBy: "",
    };

    setFilters(updatedFilters);
    setSearchSuggestions([]);
    clearAllFieldErrors();
    await fetchStalls(updatedFilters, 1, pagination.pageSize);
  };

  const handleReset = () => {
    const resetFilters = {
      search: "",
      locality: "",
      latitude: null,
      longitude: null,
      stallType: "",
      pricingType: "",
      durationType: "",
      minPrice: "",
      maxPrice: "",
      minArea: "",
      maxArea: "",
      facilities: [],
      negotiable: null,
      sortBy: "",
    };
    setFilters(resetFilters);
    setCustomStallTypeFilter("");
    resetSearchLocation();
    clearAllFieldErrors();
    fetchStalls(resetFilters, 1, pagination.pageSize);
    const modalBody = document.querySelector(
      ".st2-filter-modal .ant-modal-body"
    );

    modalBody?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handlePageChange = (page) => {
    fetchStalls(filters, page, 15);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatPrice = (price, pricingType, durationType) => {
    if (!price && price !== 0) return { amount: "Price on request", period: "" };
    const formatted = `₹${Number(price).toLocaleString("en-IN")}`;
    if (pricingType === "sale") return { amount: formatted, period: "" };
    if (durationType === "daily") return { amount: formatted, period: "/day" };
    if (durationType === "weekly") return { amount: formatted, period: "/week" };
    if (durationType === "monthly") return { amount: formatted, period: "/month" };
    if (durationType === "yearly") return { amount: formatted, period: "/year" };
    return { amount: formatted, period: "" };
  };

  const activeFiltersCount = [
    filters.locality,
    filters.stallType === "other" ? customStallTypeFilter : filters.stallType,
    filters.pricingType,
    filters.pricingType !== "sale" && filters.durationType,
    filters.minPrice,
    filters.maxPrice,
    filters.minArea,
    filters.maxArea,
    filters.facilities?.length,
    filters.negotiable !== null ? 1 : 0,
    filters.sortBy,
  ].filter(Boolean).length;

  // Duration options depend on selected pricingType
  const availableDurationOptions =
    filters.pricingType && filters.pricingType !== "sale"
      ? DURATION_TYPES_BY_PRICING[filters.pricingType] || []
      : ALL_DURATION_OPTIONS;

  // ─────────────────────────────────────────────────────────────────────────
  // FILTER DRAWER (modal content)
  // ─────────────────────────────────────────────────────────────────────────
  const filterDrawer = (
    <div className="st2-filter-panel">
      <Space orientation="vertical" size={14} style={{ width: "100%" }}>

        {/* ── Locality ──────────────────────────────────────────────────────
         *  Uses its own inline debounce + filterSuggestions state, exactly
         *  matching CommercialSpaces.  The useLocationSearch hook is NOT
         *  applied here — the Apply button is the gate for the modal.
         * ────────────────────────────────────────────────────────────────── */}
        <div className="st2-filter-group vt-field-wrap" ref={localityRef}>
          <label className="st2-filter-label">Locality</label>
          <div
            className="st2-search-custom"
            ref={searchDropdownRef}
          >
            <input
              type="text"
              className="st2-search-input"
              placeholder="Search locality"
              value={filters.locality}
              onFocus={() => {
                if (filters.locality.trim().length >= 2) {
                  filterCities(filters.locality).then((results) => {
                    setFilterSuggestions(results || []);
                  });
                }
              }}
              onChange={async (e) => {
                const value = e.target.value;

                setFilters((prev) => ({
                  ...prev,
                  locality: value,
                }));

                if (value.trim().length < 2) {
                  setFilterSuggestions([]);
                  return;
                }

                const results = await filterCities(value);

                setFilterSuggestions(results || []);
              }}
            />

            {filterSuggestions.length > 0 && (
              <div className="st2-search-dropdown-custom">
                {filterSuggestions.map((item, index) => (
                  <div
                    key={index}
                    className="st2-search-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        locality: item.value,
                      }));

                      setFilterSuggestions([]);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          <ValidationTooltip message={fieldErrors.locality} />
        </div>

        {/* ── Stall Type ────────────────────────────────────────────────── */}
        <div className="st2-filter-group">
          <label className="st2-filter-label">Stall Type</label>
          <div className="st2-custom-dropdown">
            <div
              className={`st2-custom-dropdown-input ${stallTypeDropdownOpen ? "active" : ""
                }`}
              onClick={() =>
                setStallTypeDropdownOpen(!stallTypeDropdownOpen)
              }
            >
              {filters.stallType || "Select stall type"}

              <span
                className={`st2-dropdown-arrow ${stallTypeDropdownOpen ? "rotate" : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {stallTypeDropdownOpen && (
              <div className="st2-custom-dropdown-menu">
                {ALLOWED_STALL_TYPES.map((item, index) => (
                  <div
                    key={index}
                    className={`st2-custom-dropdown-item ${filters.stallType === item
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        stallType: item,
                      }));

                      setStallTypeDropdownOpen(false);
                    }}
                  >
                    {item}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Custom stall type input ───────────────────────────────────── */}
        {filters.stallType === "other" && (
          <div
            className="st2-filter-group vt-field-wrap"
            style={{ marginTop: 8 }}
            ref={customStallTypeRef}
          >
            <label className="st2-filter-label">Custom Stall Type</label>
            <input
              type="text"
              placeholder="e.g. Art & Craft, Electronics, Fashion"
              value={customStallTypeFilter}
              maxLength={100}
              onChange={(e) => setCustomStallTypeFilter(e.target.value)}
              className={`st2-filter-input${fieldErrors.customStallType ? " vt-input-error" : ""
                }`}
            />
            <ValidationTooltip message={fieldErrors.customStallType} />
          </div>
        )}

        {/* ── Pricing Type ──────────────────────────────────────────────── */}
        <div className="st2-filter-group">
          <label className="st2-filter-label">Pricing Type</label>
          <div className="st2-custom-dropdown">
            <div
              className={`st2-custom-dropdown-input ${pricingDropdownOpen ? "active" : ""
                }`}
              onClick={() =>
                setPricingDropdownOpen(!pricingDropdownOpen)
              }
            >
              {filters.pricingType
                ? filters.pricingType.charAt(0).toUpperCase() +
                filters.pricingType.slice(1)
                : "Select pricing type"}

              <span
                className={`st2-dropdown-arrow ${pricingDropdownOpen ? "rotate" : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {pricingDropdownOpen && (
              <div className="st2-custom-dropdown-menu">
                {[
                  { value: "rent", label: "Rent" },
                  { value: "lease", label: "Lease" },
                  { value: "sale", label: "Sale" },
                ].map((item, index) => (
                  <div
                    key={index}
                    className={`st2-custom-dropdown-item ${filters.pricingType === item.value
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => {
                      const selectedPricingType =
                        ALLOWED_PRICING_TYPES.includes(item.value)
                          ? item.value
                          : "";

                      setFilters((prev) => ({
                        ...prev,
                        pricingType: selectedPricingType,
                        durationType:
                          selectedPricingType === "sale"
                            ? ""
                            : selectedPricingType === "lease" &&
                              !["monthly", "yearly"].includes(
                                prev.durationType
                              )
                              ? ""
                              : prev.durationType,
                      }));

                      setPricingDropdownOpen(false);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Duration Type — hidden when pricingType is "sale" ─────────── */}
        {filters.pricingType !== "sale" && (
          <div className="st2-filter-group">
            <label className="st2-filter-label">Duration Type</label>
            <div className="st2-custom-dropdown">
              <div
                className={`st2-custom-dropdown-input ${durationDropdownOpen ? "active" : ""
                  }`}
                onClick={() =>
                  setDurationDropdownOpen(!durationDropdownOpen)
                }
              >
                {filters.durationType || "Select duration"}

                <span
                  className={`st2-dropdown-arrow ${durationDropdownOpen ? "rotate" : ""
                    }`}
                >
                  ▼
                </span>
              </div>

              {durationDropdownOpen && (
                <div className="st2-custom-dropdown-menu">
                  {availableDurationOptions.map((item, index) => (
                    <div
                      key={index}
                      className={`st2-custom-dropdown-item ${filters.durationType === item.value
                        ? "selected"
                        : ""
                        }`}
                      onClick={() => {
                        setFilters((prev) => ({
                          ...prev,
                          durationType: item.value,
                        }));

                        setDurationDropdownOpen(false);
                      }}
                    >
                      {item.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Price Range ───────────────────────────────────────────────── */}
        <div className="st2-filter-group vt-field-wrap" ref={priceRef}>
          <label className="st2-filter-label">Price Range (₹)</label>
          <div className="st2-filter-row">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Min"
              min={0}
              max={MAX_PRICE}
              value={filters.minPrice}
              onChange={(e) => {
                const val = sanitizeNumericInput(e.target.value, MAX_PRICE);
                setFilters((p) => ({ ...p, minPrice: val }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className={`st2-filter-input-half${fieldErrors.price ? " vt-input-error" : ""
                }`}
            />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Max"
              min={0}
              max={MAX_PRICE}
              value={filters.maxPrice}
              onChange={(e) => {
                const val = sanitizeNumericInput(e.target.value, MAX_PRICE);
                setFilters((p) => ({ ...p, maxPrice: val }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className={`st2-filter-input-half${fieldErrors.price ? " vt-input-error" : ""
                }`}
            />
          </div>
          <ValidationTooltip message={fieldErrors.price} />
        </div>

        {/* ── Area ──────────────────────────────────────────────────────── */}
        <div className="st2-filter-group vt-field-wrap" ref={areaRef}>
          <label className="st2-filter-label">Area (sqft)</label>
          <div className="st2-filter-row">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Min"
              min={0}
              max={MAX_AREA}
              value={filters.minArea}
              onChange={(e) => {
                const val = sanitizeNumericInput(e.target.value, MAX_AREA);
                setFilters((p) => ({ ...p, minArea: val }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className={`st2-filter-input-half${fieldErrors.area ? " vt-input-error" : ""
                }`}
            />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Max"
              min={0}
              max={MAX_AREA}
              value={filters.maxArea}
              onChange={(e) => {
                const val = sanitizeNumericInput(e.target.value, MAX_AREA);
                setFilters((p) => ({ ...p, maxArea: val }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className={`st2-filter-input-half${fieldErrors.area ? " vt-input-error" : ""
                }`}
            />
          </div>
          <ValidationTooltip message={fieldErrors.area} />
        </div>

        {/* ── Facilities ────────────────────────────────────────────────── */}
        <div className="st2-filter-group">
          <label className="st2-filter-label">Facilities</label>
          <div className="st2-custom-dropdown">
            <div
              className={`st2-custom-dropdown-input ${facilitiesDropdownOpen ? "active" : ""
                }`}
              onClick={() =>
                setFacilitiesDropdownOpen(!facilitiesDropdownOpen)
              }
            >
              {filters.facilities?.length
                ? `${filters.facilities.length} selected`
                : "Select facilities"}

              <span
                className={`st2-dropdown-arrow ${facilitiesDropdownOpen ? "rotate" : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {facilitiesDropdownOpen && (
              <div className="st2-custom-dropdown-menu">
                {ALLOWED_FACILITIES.map((item, index) => {
                  const selected =
                    filters.facilities?.includes(item);

                  return (
                    <div
                      key={index}
                      className={`st2-custom-dropdown-item ${selected ? "selected" : ""
                        }`}
                      onClick={() => {
                        setFilters((prev) => ({
                          ...prev,
                          facilities: selected
                            ? prev.facilities.filter(
                              (f) => f !== item
                            )
                            : [...prev.facilities, item],
                        }));
                      }}
                    >
                      {item}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Negotiable ────────────────────────────────────────────────── */}
        <div className="st2-filter-group">
          <label className="st2-filter-label">Negotiable</label>
          <div className="st2-custom-dropdown">
            <div
              className={`st2-custom-dropdown-input ${negotiableDropdownOpen ? "active" : ""
                }`}
              onClick={() =>
                setNegotiableDropdownOpen(
                  !negotiableDropdownOpen
                )
              }
            >
              {filters.negotiable === null
                ? "Select negotiable"
                : filters.negotiable
                  ? "Yes"
                  : "No"}

              <span
                className={`st2-dropdown-arrow ${negotiableDropdownOpen ? "rotate" : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {negotiableDropdownOpen && (
              <div className="st2-custom-dropdown-menu">
                {[
                  { label: "Yes", value: true },
                  { label: "No", value: false },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="st2-custom-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        negotiable: item.value,
                      }));

                      setNegotiableDropdownOpen(false);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Sort By ───────────────────────────────────────────────────── */}
        <div className="st2-filter-group">
          <label className="st2-filter-label">Sort By</label>
          <div className="st2-custom-dropdown">
            <div
              className={`st2-custom-dropdown-input ${sortDropdownOpen ? "active" : ""
                }`}
              onClick={() =>
                setSortDropdownOpen(!sortDropdownOpen)
              }
            >
              {filters.sortBy || "Sort by"}

              <span
                className={`st2-dropdown-arrow ${sortDropdownOpen ? "rotate" : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {sortDropdownOpen && (
              <div className="st2-custom-dropdown-menu">
                {SORT_OPTIONS.map((item, index) => (
                  <div
                    key={index}
                    className={`st2-custom-dropdown-item ${filters.sortBy === item.value
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        sortBy: item.value,
                      }));

                      setSortDropdownOpen(false);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="st2-filter-actions">
          <Button block onClick={handleApplyFilters} className="st2-apply-btn">
            Apply Filters
          </Button>
          <Button
            block
            icon={<ReloadOutlined />}
            onClick={handleReset}
            className="st2-reset-btn"
          >
            Reset All
          </Button>
        </div>
      </Space>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="st2-page">
      <div className="st2-container">

        {/* Hero */}
        <header className="st2-hero">
          <div className="st2-hero-content">
            <span className="st2-hero-tag">Real Estate Marketplace</span>
            <h1 className="st2-hero-title">Stalls & Kiosks</h1>
            <p className="st2-hero-desc">
              Discover food stalls, retail kiosks, and exhibition spaces for your business
            </p>
          </div>
          <div className="st2-hero-decoration" aria-hidden="true">
            <div className="st2-hero-circle st2-hero-circle--lg" />
            <div className="st2-hero-circle st2-hero-circle--sm" />
          </div>
        </header>

        {/* Search & Filter Bar */}
        <section className="st2-bar">
          <div className="st2-bar-top">
            <div className="st2-bar-count">
              <strong>{pagination.total.toLocaleString()}</strong> stalls available
            </div>
            {activeFiltersCount > 0 && (
              <button className="st2-clear-all" onClick={handleReset}>
                Clear all ({activeFiltersCount})
              </button>
            )}
          </div>

          <div className="st2-bar-controls">
            {/* ── Search ─────────────────────────────────────────────────────
             *
             *  onChange  → handleSearchChange  (marks invalid, fetches suggestions)
             *            + resets all filters in state (mirrors CommercialSpaces)
             *  onSelect  → handleSearchSelect  (marks valid, does NOT fetch stalls)
             *  value     → searchInputValue    (controlled by hook)
             *  onKeyDown → makeSearchKeyDown   (Enter only fires when valid)
             *
             * ──────────────────────────────────────────────────────────────── */}
            <div className="st2-search-field">
              <SearchOutlined className="st2-search-ico" />
              <div
                className="st2-search-custom"
                ref={filterDropdownRef}
              >
                <input
                  type="text"
                  className="st2-search-input"
                  placeholder="Search by location, stall name, type, or pricing..."
                  maxLength={200}
                  value={searchInputValue}
                  onFocus={handleSearchFocus}
                  onChange={(e) => {
                    const value = e.target.value;

                    handleSearchChange(value);

                    setFilters((prev) => ({
                      ...prev,
                      search: value.slice(0, 200),
                      locality: "",
                      latitude: null,
                      longitude: null,
                      stallType: "",
                      pricingType: "",
                      durationType: "",
                      minPrice: "",
                      maxPrice: "",
                      minArea: "",
                      maxArea: "",
                      facilities: [],
                      negotiable: null,
                      sortBy: "",
                    }));

                    clearAllFieldErrors();
                  }}
                  onKeyDown={makeSearchKeyDown(handleSearchBarApply)}
                />

                {searchSuggestions.length > 0 && (
                  <div className="st2-search-dropdown-custom">
                    {searchSuggestions.map((item, index) => (
                      <div
                        key={index}
                        className="st2-search-dropdown-item"
                        onClick={() => {
                          handleSearchSelect(item.value);
                          setSearchSuggestions([]);
                        }}
                      >
                        {item.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Search Button ───────────────────────────────────────────────
             *
             *  Disabled until a valid dropdown selection has been made.
             *  Matches CommercialSpaces disabled + style pattern exactly.
             *
             * ──────────────────────────────────────────────────────────────── */}
            <button
              className="st2-search-btn"
              onClick={handleSearchBarApply}
             
            >
              Search
            </button>

            {/* Filter Button */}
            <button
              className="st2-filter-btn"
              onClick={() => setFilterModalVisible(true)}
            >
              <FilterOutlined />
              <span className="st2-filter-btn-label">Filters</span>
              {activeFiltersCount > 0 && (
                <span className="st2-filter-badge">{activeFiltersCount}</span>
              )}
            </button>
          </div>
        </section>

        {/* Modal Filter Panel */}
        <Modal
          title="Filter Stalls"
          open={filterModalVisible}
          onCancel={() => {
            setFilterModalVisible(false);
            clearAllFieldErrors();
          }}
          footer={null}
          centered
          width={modalWidth}
          className="st2-filter-modal"
          mask={{ closable: true }}
          styles={{
            body: {
              overflowY: "auto",
            },
          }}
        >
          {filterDrawer}
        </Modal>

        {/* Grid / States */}
        {loading ? (
          <div className="st2-state-wrap">
            <Spin size="large" />
            <p className="st2-state-msg">Loading stalls…</p>
          </div>
        ) : stalls.length === 0 ? (
          <div className="st2-state-wrap">
            <Empty
              description={<span className="st2-state-msg">No stalls found</span>}
            />
          </div>
        ) : (
          <>
            <Row gutter={[20, 20]} className="st2-grid">
              {stalls.map((stall) => {
                const pricingType = stall.pricing?.type || "";
                const durationType =
                  pricingType !== "sale"
                    ? stall.durationDetails?.durationType ||
                    stall.pricing?.durationType
                    : null;
                const price = formatPrice(
                  stall.pricing?.price,
                  pricingType,
                  durationType
                );
                const approvalStatus = stall.approvalStatus || "pending";
                const activeStatus = stall.status || "inactive";

                return (
                  <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={6} key={stall._id}>
                    <article className="st2-card">
                      <div className="st2-card-thumb">
                        <img
                          alt={stall.title}
                          src={
                            stall.thumbnail
                              ? stall.thumbnail + "?v=" + stall.updatedAt
                              : stall.images?.[0] ||
                              "https://images.unsplash.com/photo-1488459716781-31db52582cc9?w=800&q=80"
                          }
                          className="st2-card-img"
                          loading="lazy"
                        />
                        <div className="st2-card-overlay" />
                        <div className="st2-card-tags">
                          <span className="st2-tag st2-tag--type">
                            {stall.stallDetails?.stallType || "Stall"}
                          </span>
                          {pricingType && (
                            <span className="st2-tag st2-tag--pricing">
                              {pricingType.charAt(0).toUpperCase() +
                                pricingType.slice(1)}
                            </span>
                          )}
                          {durationType && (
                            <span className="st2-tag st2-tag--duration">
                              {durationType}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="st2-card-body">
                        <div className="st2-card-location">
                          <EnvironmentOutlined className="st2-loc-ico" />
                          <span className="st2-loc-text">
                            {stall.location?.locality || "N/A"},{" "}
                            {stall.location?.city || "N/A"},{" "}
                            {stall.location?.state || "N/A"}
                          </span>
                        </div>

                        <h3 className="st2-card-name">{stall.title}</h3>

                        <div className="st2-card-price">
                          <span className="st2-price-num">{price.amount}</span>
                          {price.period && (
                            <span className="st2-price-unit">{price.period}</span>
                          )}
                        </div>

                        <div className="st2-card-specs">
                          <div className="st2-spec-item">
                            <span className="st2-spec-ico" aria-hidden="true">
                              ▪
                            </span>
                            <span>{stall.stallDetails?.areaSqFt || 0} sqft</span>
                          </div>
                          {stall.pricing?.negotiable && (
                            <div className="st2-spec-item">
                              <span className="st2-spec-ico" aria-hidden="true">
                                ◎
                              </span>
                              <span>Negotiable</span>
                            </div>
                          )}
                        </div>

                        <div className="st2-card-footer">
                          {isAdmin && (
                            <div className="st2-card-statuses">
                              <span
                                className={`st2-pill st2-pill--${activeStatus === "active" ? "active" : "inactive"
                                  }`}
                              >
                                {activeStatus}
                              </span>
                              <span
                                className={`st2-pill st2-pill--${approvalStatus}`}
                              >
                                {approvalStatus}
                              </span>
                            </div>
                          )}
                          <Link
                            to={`/stall/${stall.slug}`}
                            className="st2-card-cta"
                          >
                            View details →
                          </Link>
                        </div>
                      </div>
                    </article>
                  </Col>
                );
              })}
            </Row>

            <div className="st2-pagination">
              <Pagination
                current={pagination.current}
                pageSize={15}
                total={pagination.total}
                onChange={handlePageChange}
                responsive
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Stalls;